import { DownloadIcon } from '@primer/octicons-react';
import { Box } from '@primer/react';
import { useContext, useEffect } from 'react';
import moment from "moment";
import * as XLSX from "xlsx";
import Button from '../../components/ui/button';
import { ILayoutContext, LayoutContext } from '../../layout/Layout';
import Block from './Block';
import ChartSiSoTheoKhoi from './ChartSiSoTheoKhoi';
import ChartSiSoTheoTrangThai from './ChartSiSoTheoTrangThai';
import ThongKeSiSoDataTable from './ThongKeSiSoDataTable';
import { useThongKeSiSo } from '../../hooks/useThongKeSiSo';

const ThongKePage = () => {
    const { setHideSubNav } = useContext<ILayoutContext>(LayoutContext);
    const { dataStructed, dmTrangThais } = useThongKeSiSo();

  useEffect(() => {
    if (setHideSubNav) {
      setHideSubNav(true);
    }
    return () => {
      if (setHideSubNav) {
        setHideSubNav(false);
      }
    };
  }, [setHideSubNav]);

    const handleExportExcel = () => {
        const exportData: any[] = [];
        const rowStyles: any[] = [];

        dataStructed.forEach((_truong, _truongId) => {
            const truongRow: any = {
                "Trường/Khối/Hệ/Lớp": _truong.name,
                "Sĩ số": _truong.si_so,
            };
      _truong.trangThaiSum.forEach((value, key) => {
        const trangThai = dmTrangThais.get(key);
        if (trangThai) {
          truongRow[trangThai.dm_trangthaihocsinh_name] = value;
        }
      });
            exportData.push(truongRow);
            rowStyles.push({ level: 0, type: 'truong' });

            _truong.items.forEach((_khoi, _khoiId) => {
                const khoiRow: any = {
                    "Trường/Khối/Hệ/Lớp": `   ${_khoi.name}`,
                    "Sĩ số": _khoi.si_so,
                };
        _khoi.trangThaiSum.forEach((value, key) => {
          const trangThai = dmTrangThais.get(key);
          if (trangThai) {
            khoiRow[trangThai.dm_trangthaihocsinh_name] = value;
          }
        });
                exportData.push(khoiRow);
                rowStyles.push({ level: 1, type: 'khoi' });

                _khoi.items.forEach((_he, _heId) => {
                    const heRow: any = {
                        "Trường/Khối/Hệ/Lớp": `      ${_he.name}`,
                        "Sĩ số": _he.si_so,
                    };
          _he.trangThaiSum.forEach((value, key) => {
            const trangThai = dmTrangThais.get(key);
            if (trangThai) {
              heRow[trangThai.dm_trangthaihocsinh_name] = value;
            }
          });
                    exportData.push(heRow);
                    rowStyles.push({ level: 2, type: 'he' });

                    _he.items.forEach((_lop, _lopId) => {
                        const lopRow: any = {
                            "Trường/Khối/Hệ/Lớp": `         ${_lop.name}`,
                            "Sĩ số": _lop.si_so,
                        };
            _lop.trangThaiSum.forEach((value, key) => {
              const trangThai = dmTrangThais.get(key);
              if (trangThai) {
                lopRow[trangThai.dm_trangthaihocsinh_name] = value;
              }
            });
                        exportData.push(lopRow);
                        rowStyles.push({ level: 3, type: 'lop' });
                    });
                });
            });
        });

        const ws = XLSX.utils.json_to_sheet(exportData);
        if (!ws["!ref"]) {
            return;
        }

        const columnWidths = [
            { wch: 45 },
            { wch: 12 },
        ];
        dmTrangThais.forEach(() => {
            columnWidths.push({ wch: 20 });
        });
        ws["!cols"] = columnWidths;

        const headerStyle = {
            font: {
                bold: true,
                color: { rgb: "FFFFFF" },
            },
            fill: {
                fgColor: { rgb: "4472C4" },
            },
            alignment: {
                horizontal: "center",
                vertical: "center",
            },
            border: {
                top: { style: "thin" },
                bottom: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" },
            },
        };

        const range = XLSX.utils.decode_range(ws["!ref"]);
        
        const headerRange = {
            s: { r: 0, c: 0 },
            e: { r: 0, c: range.e.c },
        };
        for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
            const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
            if (!ws[cellRef]) continue;
            ws[cellRef].s = headerStyle;
        }

        for (let row = 1; row <= range.e.r; row++) {
            const rowStyle = rowStyles[row - 1];
            if (!rowStyle) continue;

            let fillColor = "FFFFFF";
            let fontBold = false;
            let fontSize = 11;

            if (rowStyle.type === 'truong') {
                fillColor = "D9E1F2";
                fontBold = true;
                fontSize = 12;
            } else if (rowStyle.type === 'khoi') {
                fillColor = "E7E6E6";
                fontBold = true;
            } else if (rowStyle.type === 'he') {
                fillColor = "F2F2F2";
            } else if (rowStyle.type === 'lop') {
                fillColor = "FFFFFF";
            }

            for (let col = 0; col <= range.e.c; col++) {
                const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
                if (!ws[cellRef]) continue;

                ws[cellRef].s = {
                    font: {
                        bold: fontBold,
                        size: fontSize,
                    },
                    fill: {
                        fgColor: { rgb: fillColor },
                    },
                    alignment: {
                        horizontal: col === 0 ? "left" : "right",
                        vertical: "center",
                    },
                    border: {
                        top: { style: "thin", color: { rgb: "D0D0D0" } },
                        bottom: { style: "thin", color: { rgb: "D0D0D0" } },
                        left: { style: "thin", color: { rgb: "D0D0D0" } },
                        right: { style: "thin", color: { rgb: "D0D0D0" } },
                    },
                };
            }
        }

        ws["!rows"] = [{ hpt: 30 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Thống kê sĩ số");
        XLSX.writeFile(
            wb,
            `Thong_ke_si_so_${moment().format("DD-MM-YYYY")}.xlsx`,
            { bookType: "xlsx", type: "binary" }
        );
    };

  return (
    <Box
      sx={{
        backgroundColor: "#EFF2F4",
        minHeight: `${window.innerHeight - 90}px`,
        p: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 5,
        }}
      >
        <Box>
          <Box
            sx={{ fontWeight: "600", fontSize: 16, color: "fg.muted", mb: 2 }}
          >
            Thống kê sĩ số
          </Box>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: "1fr 1.5fr",
            }}
          >
            <Block>
            <ChartSiSoTheoKhoi />
            </Block>
            <Block>
            <ChartSiSoTheoTrangThai />
            </Block>
          </Box>
        </Box>


          
       
        <Block>
          <Box sx={{ display: "flex" }}>
            <Box sx={{ flex: 1, fontSize: 24, fontWeight: 600 }}>
              Dữ liệu sĩ số học sinh chi tiết
            </Box>
            <Box>
              <Button
                text="Tải xuống"
                leadingVisual={DownloadIcon}
                variant="invisible"
                onClick={handleExportExcel}
              />
            </Box>
          </Box>
          <Box sx={{ fontSize: "1rem", color: "fg.muted" }}>
            Sĩ số cụ thể theo từng Trường, Khối, Hệ và các trạng thái
          </Box>
          <Box sx={{ overflowX: "auto", mt: 2, width: "100%" }}>
            <ThongKeSiSoDataTable />
          </Box>
        </Block>
      </Box>
    </Box>
  );
};

export default ThongKePage;